// Firebase configuration
// 
// IMPORTANT: This config is ONLY used for WEB builds
// 
// For React Native (iOS/Android):
//   - Firebase automatically reads from GoogleService-Info.plist (iOS)
//   - Firebase automatically reads from google-services.json (Android)
//   - NO hardcoded config needed for mobile!
//
// For Web:
//   - Uses environment variables from .env file
//   - See FIREBASE_CONFIG_SETUP.md for setup instructions

// Get environment variables (only available in web builds)
// IMPORTANT: Webpack DefinePlugin replaces process.env.REACT_APP_* with actual string values
// Example: process.env.REACT_APP_FIREBASE_API_KEY becomes "AIzaSyBfnlQ3crwPuwQqRVhVzI9M6q7Dysfajus"
// The replacement happens at build time, so we MUST access them directly (not through a function)
// Webpack can only replace exact patterns like process.env.REACT_APP_FIREBASE_API_KEY

// Helper to safely get env var (handles undefined/null/string "undefined")
const safeEnvVar = (value: any): string => {
  if (!value || value === 'undefined' || value === 'null' || value === '') {
    return '';
  }
  return String(value);
};

// Production web defaults (bookaptwebapp). Firebase client keys are public; security
// is enforced via Firebase console domain rules. Env vars override when set at build time.
const FIREBASE_WEB_DEFAULTS = {
  apiKey: 'AIzaSyBfnlQ3crwPuwQqRVhVzI9M6q7Dysfajus',
  authDomain: 'bookaptwebapp.firebaseapp.com',
  projectId: 'bookaptwebapp',
  storageBucket: 'bookaptwebapp.firebasestorage.app',
  messagingSenderId: '553407421032',
  appId: '1:553407421032:web:cbd3641f85e5065f5cf9dd',
  measurementId: 'G-BF53Y72CFX',
  databaseURL: '',
} as const;

const pickConfig = (envValue: string, fallback: string): string => {
  const v = safeEnvVar(envValue);
  return v || fallback;
};

export const firebaseConfig = {
  // @ts-ignore - process.env is injected by webpack DefinePlugin
  apiKey: pickConfig(process.env.REACT_APP_FIREBASE_API_KEY, FIREBASE_WEB_DEFAULTS.apiKey),
  // @ts-ignore
  authDomain: pickConfig(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN, FIREBASE_WEB_DEFAULTS.authDomain),
  // @ts-ignore
  projectId: pickConfig(process.env.REACT_APP_FIREBASE_PROJECT_ID, FIREBASE_WEB_DEFAULTS.projectId),
  // @ts-ignore
  storageBucket: pickConfig(
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    FIREBASE_WEB_DEFAULTS.storageBucket,
  ),
  // @ts-ignore
  messagingSenderId: pickConfig(
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    FIREBASE_WEB_DEFAULTS.messagingSenderId,
  ),
  // @ts-ignore
  appId: pickConfig(process.env.REACT_APP_FIREBASE_APP_ID, FIREBASE_WEB_DEFAULTS.appId),
  // @ts-ignore
  measurementId: pickConfig(
    process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    FIREBASE_WEB_DEFAULTS.measurementId,
  ),
  // @ts-ignore
  databaseURL: pickConfig(
    process.env.REACT_APP_FIREBASE_DATABASE_URL,
    FIREBASE_WEB_DEFAULTS.databaseURL,
  ),
};

// Validate that required config is present (for web builds only)
if (typeof window !== 'undefined') {
  const required = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  const missing = required.filter(key => !firebaseConfig[key as keyof typeof firebaseConfig]);
  
  // Debug: Log what we got
  // Note: In browser, webpack DefinePlugin has replaced process.env.REACT_APP_* with actual values
  // So we can't check Object.keys(process.env) - process doesn't exist in browser
  console.log('🔍 Firebase Config Debug:', {
    hasApiKey: !!firebaseConfig.apiKey,
    hasProjectId: !!firebaseConfig.projectId,
    apiKeyLength: firebaseConfig.apiKey?.length || 0,
    apiKeyPreview: firebaseConfig.apiKey ? firebaseConfig.apiKey.substring(0, 20) + '...' : 'missing',
    projectId: firebaseConfig.projectId || 'missing',
    authDomain: firebaseConfig.authDomain || 'missing',
    expectedProjectId: 'bookaptwebapp',
    projectMatches: firebaseConfig.projectId === 'bookaptwebapp',
  });
  
  // Warn if project ID doesn't match expected
  if (firebaseConfig.projectId && firebaseConfig.projectId !== 'bookaptwebapp') {
    console.warn('⚠️ WARNING: Firebase projectId does not match expected "bookaptwebapp"');
    console.warn('⚠️ Current projectId:', firebaseConfig.projectId);
    console.warn('⚠️ This may cause Auth to connect to a different project than Firestore!');
    console.warn('⚠️ Check your .env file: REACT_APP_FIREBASE_PROJECT_ID should be "bookaptwebapp"');
  }
  
  if (missing.length > 0) {
    console.warn('⚠️ Missing Firebase config for web:', missing);
    console.warn('💡 Create a .env file with REACT_APP_FIREBASE_* variables');
    console.warn('💡 See FIREBASE_CONFIG_SETUP.md for instructions');
    console.warn('💡 Restart webpack dev server after adding .env file');
  } else {
    console.log('✅ Firebase config loaded successfully');
  }
}
